import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  HomeIcon,
  MagnifyingGlassIcon,
  UserIcon,
  SunIcon,
  MoonIcon,
} from '@heroicons/react/24/outline';

const NAV_ITEMS = [
  { id: 'home', icon: HomeIcon, label: 'Home', href: '/' },
  { id: 'search', icon: MagnifyingGlassIcon, label: 'Search', href: '/search' },
  { id: 'profile', icon: UserIcon, label: 'Profile', href: '/settings' },
] as const;

export function FloatingNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isDark, setIsDark] = useState(true);
  const [bounceTheme, setBounceTheme] = useState(false);

  const activeIndex = NAV_ITEMS.findIndex((item) => {
    if (item.href === '/') return location.pathname === '/';
    return location.pathname.startsWith(item.href);
  });
  const resolvedIndex = activeIndex === -1 ? 0 : activeIndex;

  const toggleTheme = useCallback(() => {
    setIsDark((prev) => !prev);
    setBounceTheme(true);
    // Toggle dark class on document
    document.documentElement.classList.toggle('dark');
  }, []);

  useEffect(() => {
    if (bounceTheme) {
      const timer = setTimeout(() => setBounceTheme(false), 400);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [bounceTheme]);

  // Ring position: each button is 40px wide (p-2.5=10px*2 + icon 20px), with 4px gaps
  const buttonWidth = 40;
  const gap = 4;
  const totalNavWidth = NAV_ITEMS.length * buttonWidth + (NAV_ITEMS.length - 1) * gap;
  const ringOffset = resolvedIndex * (buttonWidth + gap) + buttonWidth / 2 - totalNavWidth / 2;

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
      <div className="flex items-center gap-3 px-4 py-2.5 rounded-2xl bg-white/[0.07] backdrop-blur-xl border border-white/[0.08] shadow-glass">
        {/* Nav buttons with gold ring */}
        <div className="relative flex items-center gap-1">
          {/* Gold ring indicator - 4 layers */}
          <div
            className="absolute w-11 h-11 -translate-x-1/2 transition-all duration-500 ease-bouncy-slide pointer-events-none"
            style={{ left: `calc(50% + ${ringOffset}px)` }}
          >
            {/* Layer 1: Outer glow */}
            <div className="absolute inset-0 rounded-full bg-primary-500/20 blur-md" />
            {/* Layer 2: Clip container */}
            <div className="absolute inset-0 rounded-full overflow-hidden">
              {/* Layer 3: Rotating conic gradient */}
              <div
                className="absolute inset-[-2px] animate-spin-gold"
                style={{
                  background: 'conic-gradient(from 0deg, #c9a633, #e8d48b, #c9a633, #8b7320, #c9a633)',
                }}
              />
            </div>
            {/* Layer 4: Inner plate */}
            <div className="absolute inset-[2px] rounded-full bg-[#0a0a0f]" />
          </div>

          {NAV_ITEMS.map((item, index) => (
            <button
              key={item.id}
              onClick={() => navigate(item.href)}
              className={`
                relative z-10 p-2.5 rounded-full transition-colors duration-200
                ${index === resolvedIndex
                  ? 'text-primary-400'
                  : 'text-gray-500 hover:text-gray-300'
                }
              `}
              title={item.label}
            >
              <item.icon className="h-5 w-5" strokeWidth={1.5} />
            </button>
          ))}
        </div>

        {/* Divider */}
        <div className="w-px h-6 bg-white/10" />

        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className="relative p-2.5 rounded-full text-gray-500 hover:text-gray-300 transition-colors duration-200 overflow-hidden"
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          <div className={bounceTheme ? 'animate-theme-bounce' : ''}>
            {isDark ? (
              <SunIcon className="h-5 w-5" strokeWidth={1.5} />
            ) : (
              <MoonIcon className="h-5 w-5" strokeWidth={1.5} />
            )}
          </div>
        </button>
      </div>
    </div>
  );
}
